/*

  UserPrefsDialog.ts

  User preferences dialog component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, UserErrors } from "./types";
import { ComponentFunc, m, VnodeDOM, VnodeChildren } from "./mithril";
import { glyph, nbsp, getInput } from "./util";
import { mt, t, ts } from "./i18n";
import {
  DialogButton,
  TextInput,
  TogglerAudio,
  TogglerFanfare,
  TogglerBeginner,
  TogglerFairplay
} from "./components";
import { logEvent } from "./channel";

interface IAttributes {
  view: IView;
}

export const UserPrefsDialog: ComponentFunc<IAttributes> = (initialVnode) => {

  // A dialog showing the preferences of the current user

  const { view } = initialVnode.attrs;
  const { model } = view;

  function vwErrMsg(propname: keyof UserErrors) {
    // Show a validation error message returned from the server
    const err = model.userErrors || {};
    return err.hasOwnProperty(propname) ?
      m(".errinput", [glyph("arrow-up"), nbsp(), err[propname] || ""]) : "";
  }

  function getToggle(elemId: string): boolean {
    const cls2 = document.querySelector("#" + elemId + "-toggler #opt2")?.classList;
    if (!cls2) return false;
    return cls2.contains("selected");
  }

  function validate() {
    // Move data back to the model.user object
    // before sending it to the server
    const user = model.user;
    if (!user) return;
    user.nickname = getInput("nickname");
    user.full_name = getInput("full_name");
    user.audio = getToggle("audio");
    user.fanfare = getToggle("fanfare");
    user.beginner = getToggle("beginner");
    user.fairplay = getToggle("fairplay");
    // When done, pop the current dialog
    model.saveUser(() => { view.popDialog(); });
  }

  function initFocus(vnode: VnodeDOM) {
    // Set the focus on the nickname field when the dialog is displayed
    (vnode.dom.querySelector("#nickname") as HTMLElement).focus();
  }

  function vwDialogButton(
    id: string, title: string, onclick: (ev: Event) => void,
    content: VnodeChildren, tabindex: number
  ): VnodeChildren {
    // Create a .modal-close dialog button
    const attrs = {
      id,
      onclick,
      title,
      tabindex
    };
    return m(DialogButton, attrs, content);
  }

  return {

    view: () => {
      if (!model.user) return undefined;
      const user = model.user;
      if (!model.state) return undefined;
      const state = model.state;

      return m(".modal-dialog",
        {
          key: "userprefs",
          id: "user-dialog",
          oncreate: initFocus
        },
        m(".ui-widget.ui-widget-content.ui-corner-all", { id: 'user-form' },
          [
            m(".loginhdr", [glyph("address-book"), " " + ts("player_info")]), // "Player information"
            m("div",
              m("form", { action: '', id: 'frm1', method: 'post', name: 'frm1' },
                [
                  m(".dialog-spacer",
                    [
                      m("span.caption", t("Einkenni:")),
                      m(TextInput,
                        {
                          initialValue: user.nickname || "",
                          class: "username",
                          maxlength: 15,
                          id: "nickname",
                          // autocomplete: "nickname", // Chrome doesn't like this
                        }
                      ),
                      nbsp(), m("span.asterisk", "*")
                    ]
                  ),
                  m(".explain", t("Verður að vera útfyllt")),
                  vwErrMsg("nickname"),
                  m(".dialog-spacer",
                    [
                      m("span.caption", t("Fullt nafn:")),
                      m(TextInput,
                        {
                          initialValue: user.full_name || "",
                          class: "fullname",
                          maxlength: 32,
                          id: "full_name",
                          autocomplete: "name",
                        }
                      )
                    ]
                  ),
                  m(".explain", t("Valfrjálst - sýnt í notendalistum Netskrafls")),
                  vwErrMsg("full_name"),
                  m(".dialog-spacer",
                    [
                      m("span.caption.sub", t("Hljóðmerki:")),
                      m(TogglerAudio, { view, state: user.audio, tabindex: 4 }),
                      m("span.subcaption", t("Lúðraþytur eftir sigur:")),
                      m(TogglerFanfare, { view, state: user.fanfare, tabindex: 5 }),
                    ]
                  ),
                  m(".explain", t("explain_sound")),
                  m(".dialog-spacer",
                    [
                      m("span.caption.sub", t("Sýna reitagildi:")),
                      m(TogglerBeginner, { view, state: user.beginner, tabindex: 6 }),
                      mt(".subexplain",
                        [
                          "Stillir hvort ",
                          mt("strong", "minnismiði"),
                          " um margföldunargildi reita er sýndur við borðið"
                        ]
                      )
                    ]
                  ),
                  m(".dialog-spacer",
                    [
                      m("span.caption.sub", t("Án hjálpartækja:")),
                      m(TogglerFairplay, { view, state: user.fairplay, tabindex: 7 }),
                      mt(".subexplain",
                        [
                          "no_helpers",
                          mt("strong", "án stafrænna hjálpartækja"),
                          " af nokkru tagi"
                        ]
                      )
                    ]
                  )
                ]
              )
            ),
            vwDialogButton("user-ok", ts("Vista"), validate, glyph("ok"), 8),
            vwDialogButton("user-cancel", ts("Hætta við"),
              (ev) => { view.popDialog(); ev.preventDefault(); },
              glyph("remove"), 9),
            user.friend ?
              vwDialogButton("user-unfriend", ts("Þú ert áskrifandi!"),
                (ev) => {
                  ev.preventDefault();
                },
                [glyph("coffee-cup"), ts("Þú ert áskrifandi!")], 10
              )
              :
              vwDialogButton("user-friend", ts("Gerast áskrifandi"),
                (ev) => {
                  // Open the container's subscription page
                  ev.preventDefault();
                  logEvent("click_friend",
                    {
                      userid: state.netskraflUserId, locale: state.locale
                    }
                  );
                  // Navigate to the container-supplied URL
                  window.location.href = state.subscriptionUrl;
                },
                [glyph("coffee-cup"), nbsp(), nbsp(), ts("Gerast áskrifandi")], 11
              )
          ]
        )
      );
    }
  };

};
